import { NumberField } from "@/components/ui/number-field";
import { useState, useEffect, useRef } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { AutoExpandingTextarea } from "@/components/auto-expanding-textarea";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Clock, Plus, Trash2, Users, BookOpen, ListTodo, DollarSign, CheckSquare, Target, X, HelpCircle } from "lucide-react";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { apiRequest } from "@/lib/queryClient";
import { type Goal, type Category, type GoalPlanningItem } from "@shared/schema";
import { CategorySelectGroups } from "@/components/category-select-groups";
import { formatDateInput, parseLocalDate } from "@/lib/dateUtils";
import { useToast } from "@/hooks/use-toast";
import { DialogDescription, DialogFooter } from "@/components/ui/dialog";
import TrackerXMeasurementSection, {
  type NewTrackerFields,
  type TrackerXRecord,
} from "@/components/trackerx-measurement-section";

interface EditGoalModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  goal: Goal;
}

const formSchema = z.object({
  title: z.string().min(1, "Title is required"),
  description: z.string().optional(),
  whyReason1: z.string().optional(),
  whyReason2: z.string().optional(),
  whyReason3: z.string().optional(),
  whyReason4: z.string().optional(),
  whyReason5: z.string().optional(),
  category: z.string().min(1, "Category is required"),
  priority: z.enum(["low", "medium", "high", "urgent"]).default("medium"),
  status: z.enum(["active", "completed", "paused", "future"]).default("active"),
  progress: z.number().int().min(0).max(100).default(0),
  progressCalculationType: z.enum(["manual", "milestones", "tasks", "habits", "custom", "trackerx"]).default("manual"),
  habitTargetCount: z.coerce.number<number>().optional(),
  customGoalType: z.enum(["number", "currency"]).optional(),
  customGoalTitle: z.string().optional(),
  customGoalTarget: z.coerce.number<number>().optional(),
  targetValue: z.string().optional(),
  currentValue: z.string().optional(),
  currentValueRaw: z.number().optional(),
  deadline: z.string().optional(),
  isFutureGoal: z.boolean().default(false),
  mediaGalleryExpanded: z.boolean().default(false),
  mediaSlideshowMode: z.string().default("static"),
  mediaSlideshowInterval: z.number().int().default(3),
  smartSpecific: z.boolean().default(false),
  smartMeasurable: z.boolean().default(false),
  smartAchievable: z.boolean().default(false),
  smartRelevant: z.boolean().default(false),
  smartTimeBound: z.boolean().default(false),
  smartBypassed: z.boolean().default(false),
  timeCost: z.coerce.number<number>().optional(),
  timeCostFrequency: z.enum(["daily", "weekly", "monthly"]).optional(),
  measuredTrackingMode: z.string().default("sum"),
  goalSource: z.string().optional(),
  healthMetric: z.string().optional(),
});

const taskFormSchema = z.object({
  title: z.string().min(1, "Title is required"),
  description: z.string().optional(),
  dueDate: z.string().optional(),
  showOnMainTaskPage: z.boolean().default(true),
  category: z.string().default("Uncategorized"),
  priority: z.enum(["low", "medium", "high"]).default("medium"),
  assignedTo: z.enum(["Me", "Others"]).default("Me"),
  assignedToOther: z.string().optional(),
});

type TaskFormInput = z.input<typeof taskFormSchema>;
type TaskFormData = z.output<typeof taskFormSchema>;

type FormInput = z.input<typeof formSchema>;
type FormData = z.output<typeof formSchema>;

export default function EditGoalModal({ open, onOpenChange, goal }: EditGoalModalProps) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [createTaskDialogOpen, setCreateTaskDialogOpen] = useState(false);
  const [pendingTaskText, setPendingTaskText] = useState("");
  const [pendingItemId, setPendingItemId] = useState("");
  const [taskTags, setTaskTags] = useState<string[]>([]);
  const [currentTaskTag, setCurrentTaskTag] = useState("");
  const [taskAssignedTo, setTaskAssignedTo] = useState<"Me" | "Others">("Me");

  // Fetch categories from the database (include goals-only categories)
  const { data: categories = [] } = useQuery<Category[]>({
    queryKey: ["/api/categories?includeGoalsOnly=true"],
  });

  // Fetch planning items for this goal
  const { data: planningItems = [] } = useQuery<GoalPlanningItem[]>({
    queryKey: ["/api/goals", goal.id, "planning-items"],
    enabled: open,
  });

  // Task creation form
  const taskForm = useForm<TaskFormInput, any, TaskFormData>({
    resolver: zodResolver(taskFormSchema),
    defaultValues: {
      title: "",
      description: "",
      dueDate: "",
      showOnMainTaskPage: true,
      category: "Uncategorized",
      priority: "medium",
      assignedTo: "Me",
      assignedToOther: "",
    },
  });

  // Idempotency key: generated once per submission attempt and reused on
  // network-error retries so the server can deduplicate. Cleared on success,
  // whenever the create dialog is closed, and whenever the outer modal itself
  // is closed (so that a reopen always starts with a fresh key even if the
  // inner dialog was left open when the outer modal dismissed).
  const createTaskIdempotencyKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (!createTaskDialogOpen || !open) {
      createTaskIdempotencyKeyRef.current = null;
    }
  }, [createTaskDialogOpen, open]);

  // Task creation mutation
  const createTaskMutation = useMutation({
    mutationFn: async (data: TaskFormData) => {
      const taskData = {
        title: data.title,
        description: data.description || null,
        completed: false,
        category: data.category || "Uncategorized",
        priority: data.priority || "medium",
        tags: taskTags,
        assignedTo: taskAssignedTo,
        assignedToOther: taskAssignedTo === "Others" ? data.assignedToOther : null,
        dueDate: data.dueDate ? parseLocalDate(data.dueDate).toISOString() : null,
        goalId: goal.id,
        showOnMainTaskPage: data.showOnMainTaskPage ?? true,
      };
      if (!createTaskIdempotencyKeyRef.current) {
        createTaskIdempotencyKeyRef.current = crypto.randomUUID();
      }
      const response = await apiRequest("POST", "/api/tasks", taskData, {
        "Idempotency-Key": createTaskIdempotencyKeyRef.current,
      });
      return response.json();
    },
    onSuccess: async (task) => {
      createTaskIdempotencyKeyRef.current = null;
      if (pendingItemId) {
        await apiRequest("PATCH", `/api/goals/${goal.id}/planning-items/${pendingItemId}`, {
          convertedToTaskId: task.id,
        });
      }
      queryClient.invalidateQueries({ queryKey: ["/api/goals", goal.id, "planning-items"] });
      queryClient.invalidateQueries({ queryKey: ["/api/tasks"] });
      queryClient.invalidateQueries({ queryKey: ["/api/goals"] });
      setCreateTaskDialogOpen(false);
      taskForm.reset();
      setTaskTags([]);
      setCurrentTaskTag("");
      setTaskAssignedTo("Me");
      setPendingTaskText("");
      setPendingItemId("");
      toast({ title: "Task created successfully" });
    },
    onError: () => {
      toast({ title: "Failed to create task", variant: "destructive" });
    },
  });

  const handleOpenCreateTask = (text: string, itemId: string) => {
    setPendingTaskText(text);
    setPendingItemId(itemId);
    setTaskTags([]);
    setCurrentTaskTag("");
    setTaskAssignedTo("Me");
    taskForm.reset({
      title: text,
      description: "",
      dueDate: "",
      showOnMainTaskPage: true,
      category: goal.category || "Uncategorized",
      priority: "medium",
      assignedTo: "Me",
      assignedToOther: "",
    });
    setCreateTaskDialogOpen(true);
  };

  const handleAddTaskTag = () => {
    if (currentTaskTag.trim() && !taskTags.includes(currentTaskTag.trim())) {
      setTaskTags([...taskTags, currentTaskTag.trim()]);
      setCurrentTaskTag("");
    }
  };

  const handleRemoveTaskTag = (tagToRemove: string) => {
    setTaskTags(taskTags.filter(tag => tag !== tagToRemove));
  };

  const onTaskSubmit = (data: TaskFormData) => {
    createTaskMutation.mutate(data);
  };

  // Planning items mutations
  const createPlanningItemMutation = useMutation({
    mutationFn: (data: { type: string; content: string }) =>
      apiRequest("POST", `/api/goals/${goal.id}/planning-items`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/goals", goal.id, "planning-items"] });
    },
    onError: () => {
      toast({ title: "Couldn't add planning item", description: "Please try again.", variant: "destructive" });
    },
  });

  const updatePlanningItemMutation = useMutation({
    mutationFn: ({ id, content }: { id: string; content: string }) =>
      apiRequest("PATCH", `/api/goals/${goal.id}/planning-items/${id}`, { content }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/goals", goal.id, "planning-items"] });
    },
    onError: () => {
      toast({ title: "Couldn't save planning item", description: "Your change was not saved. Please try again.", variant: "destructive" });
    },
  });

  const deletePlanningItemMutation = useMutation({
    mutationFn: (id: string) =>
      apiRequest("DELETE", `/api/goals/${goal.id}/planning-items/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/goals", goal.id, "planning-items"] });
    },
    onError: () => {
      toast({ title: "Couldn't delete planning item", description: "Please try again.", variant: "destructive" });
    },
  });

  const handleAddPlanningItem = (type: string) => {
    createPlanningItemMutation.mutate({ type, content: "" });
  };

  const handleUpdatePlanningItem = (id: string, content: string) => {
    updatePlanningItemMutation.mutate({ id, content });
  };

  const handleDeletePlanningItem = (id: string) => {
    deletePlanningItemMutation.mutate(id);
  };

  const form = useForm<FormInput, any, FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      title: goal.title,
      description: goal.description || "",
      whyReason1: goal.whyReason1 || "",
      whyReason2: goal.whyReason2 || "",
      whyReason3: goal.whyReason3 || "",
      whyReason4: goal.whyReason4 || "",
      whyReason5: goal.whyReason5 || "",
      category: goal.category ?? "",
      priority: (goal.priority ?? "medium") as "low" | "medium" | "high" | "urgent",
      status: (goal.status === "future" || goal.isFutureGoal ? "future" : goal.status ?? "active") as "active" | "completed" | "paused" | "future",
      progress: goal.progress ?? 0,
      targetValue: goal.targetValue || "",
      currentValue: goal.currentValue || "",
      deadline: goal.deadline ? formatDateInput(new Date(goal.deadline)) : "",
      progressCalculationType: (goal.progressCalculationType || "manual") as "manual" | "milestones" | "tasks" | "habits" | "custom" | "trackerx",
      habitTargetCount: goal.habitTargetCount || undefined,
      customGoalType: (goal.customGoalType || undefined) as "number" | "currency" | undefined,
      customGoalTitle: goal.customGoalTitle || "",
      customGoalTarget: goal.customGoalTarget || undefined,
      isFutureGoal: goal.status === "future" || goal.isFutureGoal || false,
      timeCost: goal.timeCost || undefined,
      timeCostFrequency: (goal.timeCostFrequency || undefined) as "daily" | "weekly" | "monthly" | undefined,
    },
  });

  const progressType = form.watch("progressCalculationType");

  // TrackerX measurement linking state
  const [selectedTrackerIds, setSelectedTrackerIds] = useState<string[]>([]);
  const [newTrackers, setNewTrackers] = useState<NewTrackerFields[]>([]);
  const [trackerError, setTrackerError] = useState("");

  const { data: linkedTrackers = [] } = useQuery<TrackerXRecord[]>({
    queryKey: ["/api/goals", goal.id, "trackerx"],
    queryFn: async () => {
      const res = await apiRequest("GET", `/api/goals/${goal.id}/trackerx`);
      return res.json();
    },
    enabled: open,
  });

  // Initialize selection from the currently linked trackers when the dialog opens
  useEffect(() => {
    if (!open) return;
    setSelectedTrackerIds(linkedTrackers.map((t) => t.id));
    setNewTrackers([]);
    setTrackerError("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, linkedTrackers.map((t) => t.id).sort().join(",")]);

  // Update form when goal changes
  useEffect(() => {
    form.reset({
      title: goal.title,
      description: goal.description || "",
      whyReason1: goal.whyReason1 || "",
      whyReason2: goal.whyReason2 || "",
      whyReason3: goal.whyReason3 || "",
      whyReason4: goal.whyReason4 || "",
      whyReason5: goal.whyReason5 || "",
      category: goal.category ?? "",
      priority: (goal.priority ?? "medium") as "low" | "medium" | "high" | "urgent",
      status: (goal.status === "future" || goal.isFutureGoal ? "future" : goal.status ?? "active") as "active" | "completed" | "paused" | "future",
      progress: goal.progress ?? 0,
      targetValue: goal.targetValue || "",
      currentValue: goal.currentValue || "",
      deadline: goal.deadline ? formatDateInput(new Date(goal.deadline)) : "",
      progressCalculationType: (goal.progressCalculationType || "manual") as "manual" | "milestones" | "tasks" | "habits" | "custom" | "trackerx",
      habitTargetCount: goal.habitTargetCount || undefined,
      customGoalType: (goal.customGoalType || undefined) as "number" | "currency" | undefined,
      customGoalTitle: goal.customGoalTitle || "",
      customGoalTarget: goal.customGoalTarget || undefined,
      isFutureGoal: goal.status === "future" || goal.isFutureGoal || false,
      timeCost: goal.timeCost || undefined,
      timeCostFrequency: (goal.timeCostFrequency || undefined) as "daily" | "weekly" | "monthly" | undefined,
    });
  }, [goal, form]);

  const updateGoalMutation = useMutation({
    mutationFn: async (data: FormData) => {
      const goalData = {
        ...data,
        deadline: data.deadline ? new Date(data.deadline) : null,
        whyReason1: data.whyReason1 || null,
        whyReason2: data.whyReason2 || null,
        whyReason3: data.whyReason3 || null,
        whyReason4: data.whyReason4 || null,
        whyReason5: data.whyReason5 || null,
        progressCalculationType: data.progressCalculationType || "manual",
        habitTargetCount: data.habitTargetCount || null,
        customGoalType: data.customGoalType || null,
        customGoalTitle: data.customGoalTitle || null,
        customGoalTarget: data.customGoalTarget || null,
        status: data.status === "future" || data.isFutureGoal ? "future" : data.status,
        isFutureGoal: data.status === "future" || data.isFutureGoal,
        timeCost: data.timeCost || null,
        timeCostFrequency: data.timeCostFrequency || null,
      };
      
      const response = await apiRequest("PATCH", `/api/goals/${goal.id}`, goalData);
      const updatedGoal = await response.json();

      // Sync TrackerX linking with the selected measurement method
      if (data.progressCalculationType === "trackerx") {
        const linkedIds = new Set(linkedTrackers.map((t) => t.id));
        const selectedIds = new Set(selectedTrackerIds);
        const ops: Promise<unknown>[] = [
          // Unlink deselected trackers
          ...linkedTrackers
            .filter((t) => !selectedIds.has(t.id))
            .map((t) => apiRequest("PATCH", `/api/trackerx/${t.id}`, { goalId: null })),
          // Link newly selected trackers
          ...selectedTrackerIds
            .filter((id) => !linkedIds.has(id))
            .map((id) => apiRequest("PATCH", `/api/trackerx/${id}`, { goalId: goal.id })),
        ];
        for (const newTracker of newTrackers) {
          ops.push(apiRequest("POST", "/api/trackerx", {
            name: newTracker.name.trim(),
            // Inherit the goal's category (e.g. "Health") so reports and
            // percentages group the tracker with the goal it measures.
            ...(data.category ? { category: data.category } : {}),
            valueType: newTracker.valueType,
            unitLabel: newTracker.unitLabel.trim() || null,
            period: newTracker.period,
            goalValue: newTracker.goalValue !== "" ? Number(newTracker.goalValue) : null,
            goalId: goal.id,
          }));
        }
        await Promise.all(ops);
        queryClient.invalidateQueries({ queryKey: ["/api/trackerx"] });
        queryClient.invalidateQueries({ queryKey: ["/api/goals", goal.id, "trackerx"] });
      } else if (linkedTrackers.length > 0) {
        // Measurement method changed away from TrackerX — unlink previously linked trackers
        await Promise.all(
          linkedTrackers.map((t) => apiRequest("PATCH", `/api/trackerx/${t.id}`, { goalId: null }))
        );
        queryClient.invalidateQueries({ queryKey: ["/api/trackerx"] });
        queryClient.invalidateQueries({ queryKey: ["/api/goals", goal.id, "trackerx"] });
      }

      return updatedGoal;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/goals"] });
      queryClient.invalidateQueries({ queryKey: ["/api/stats/dashboard"] });
      queryClient.invalidateQueries({ queryKey: ["/api/stats/categories"] });
      queryClient.invalidateQueries({ queryKey: ["/api/activities"] });
      
      onOpenChange(false);
    },
    onError: (error: any) => {
      toast({
        title: "Couldn't save the goal",
        description: error?.message || "Something went wrong. Please try again.",
        variant: "destructive",
      });
    },
  });

  const onSubmit = (data: FormData) => {
    if (data.progressCalculationType === "trackerx") {
      if (selectedTrackerIds.length === 0 && newTrackers.length === 0) {
        setTrackerError("Select at least one TrackerX record or create a new one.");
        return;
      }
      if (newTrackers.some((t) => !t.name.trim())) {
        setTrackerError("Enter a name for each new TrackerX record.");
        return;
      }
      if (newTrackers.some((t) => !(Number.isFinite(Number(t.goalValue)) && Number(t.goalValue) > 0))) {
        setTrackerError("Enter a target value greater than 0 for each new TrackerX record.");
        return;
      }
    }
    setTrackerError("");
    updateGoalMutation.mutate(data);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="modal-edit-goal" className="w-screen h-screen max-w-none max-h-none m-0 rounded-none overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Goal</DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            {/* Goal Title */}
            <FormField
              control={form.control}
              name="title"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Goal Title</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="What do you want to achieve?"
                      {...field}
                      data-testid="input-edit-goal-title"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Description */}
            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description</FormLabel>
                  <FormControl>
                    <AutoExpandingTextarea
                      placeholder="Describe your goal in detail..."
                      minRows={3}
                      maxRows={15}
                      {...field}
                      value={field.value || ""}
                      data-testid="textarea-edit-goal-description"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Goal Planning Sections */}
            <div className="space-y-6">
              {/* Who Do I Have to Know? */}
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Users className="w-4 h-4 text-primary" />
                  <h3 className="text-base font-semibold">Who Do I Have to Know?</h3>
                </div>
                {planningItems.filter(item => item.type === "who_know").map((item) => (
                  <div key={item.id} className="flex items-center gap-2">
                    <Input
                      defaultValue={item.content}
                      onBlur={(e) => {
                        e.stopPropagation();
                        if (e.target.value !== item.content) {
                          handleUpdatePlanningItem(item.id, e.target.value);
                        }
                      }}
                      onKeyDown={(e) => {
                        e.stopPropagation();
                        if (e.key === "Enter") {
                          e.preventDefault();
                          (e.target as HTMLInputElement).blur();
                        }
                      }}
                      placeholder="Enter person or connection..."
                      disabled={!!item.convertedToTaskId}
                      data-testid={`input-planning-who-${item.id}`}
                    />
                    {item.convertedToTaskId ? (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled
                        className="flex-shrink-0"
                        data-testid={`button-task-created-who-${item.id}`}
                      >
                        <CheckSquare className="w-4 h-4 mr-1" />
                        Task Created
                      </Button>
                    ) : (
                      <Button
                        type="button"
                        variant="default"
                        size="sm"
                        onClick={() => handleOpenCreateTask(item.content, item.id)}
                        disabled={!item.content?.trim()}
                        className="flex-shrink-0"
                        data-testid={`button-create-task-who-${item.id}`}
                      >
                        Create Task
                      </Button>
                    )}
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDeletePlanningItem(item.id)}
                      className="text-muted-foreground hover:text-destructive"
                      data-testid={`button-delete-planning-who-${item.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => handleAddPlanningItem("who_know")}
                  data-testid="button-add-who-know"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Add Another Record
                </Button>
              </div>

              {/* What Do I Need to Know? */}
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <BookOpen className="w-4 h-4 text-primary" />
                  <h3 className="text-base font-semibold">What Do I Need to Know?</h3>
                </div>
                {planningItems.filter(item => item.type === "what_know").map((item) => (
                  <div key={item.id} className="flex items-center gap-2">
                    <Input
                      defaultValue={item.content}
                      onBlur={(e) => {
                        e.stopPropagation();
                        if (e.target.value !== item.content) {
                          handleUpdatePlanningItem(item.id, e.target.value);
                        }
                      }}
                      onKeyDown={(e) => {
                        e.stopPropagation();
                        if (e.key === "Enter") {
                          e.preventDefault();
                          (e.target as HTMLInputElement).blur();
                        }
                      }}
                      placeholder="Enter knowledge or skill..."
                      disabled={!!item.convertedToTaskId}
                      data-testid={`input-planning-what-know-${item.id}`}
                    />
                    {item.convertedToTaskId ? (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled
                        className="flex-shrink-0"
                        data-testid={`button-task-created-what-know-${item.id}`}
                      >
                        <CheckSquare className="w-4 h-4 mr-1" />
                        Task Created
                      </Button>
                    ) : (
                      <Button
                        type="button"
                        variant="default"
                        size="sm"
                        onClick={() => handleOpenCreateTask(item.content, item.id)}
                        disabled={!item.content?.trim()}
                        className="flex-shrink-0"
                        data-testid={`button-create-task-what-know-${item.id}`}
                      >
                        Create Task
                      </Button>
                    )}
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDeletePlanningItem(item.id)}
                      className="text-muted-foreground hover:text-destructive"
                      data-testid={`button-delete-planning-what-know-${item.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => handleAddPlanningItem("what_know")}
                  data-testid="button-add-what-know"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Add Another Record
                </Button>
              </div>

              {/* What Do I Have to Do? */}
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <ListTodo className="w-4 h-4 text-primary" />
                  <h3 className="text-base font-semibold">What Do I Have to Do?</h3>
                </div>
                {planningItems.filter(item => item.type === "what_do").map((item) => (
                  <div key={item.id} className="flex items-center gap-2">
                    <Input
                      defaultValue={item.content}
                      onBlur={(e) => {
                        e.stopPropagation();
                        if (e.target.value !== item.content) {
                          handleUpdatePlanningItem(item.id, e.target.value);
                        }
                      }}
                      onKeyDown={(e) => {
                        e.stopPropagation();
                        if (e.key === "Enter") {
                          e.preventDefault();
                          (e.target as HTMLInputElement).blur();
                        }
                      }}
                      placeholder="Enter action item..."
                      disabled={!!item.convertedToTaskId}
                      data-testid={`input-planning-what-do-${item.id}`}
                    />
                    {item.convertedToTaskId ? (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled
                        className="flex-shrink-0"
                        data-testid={`button-task-created-what-do-${item.id}`}
                      >
                        <CheckSquare className="w-4 h-4 mr-1" />
                        Task Created
                      </Button>
                    ) : (
                      <Button
                        type="button"
                        variant="default"
                        size="sm"
                        onClick={() => handleOpenCreateTask(item.content, item.id)}
                        disabled={!item.content?.trim()}
                        className="flex-shrink-0"
                        data-testid={`button-create-task-what-do-${item.id}`}
                      >
                        Create Task
                      </Button>
                    )}
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDeletePlanningItem(item.id)}
                      className="text-muted-foreground hover:text-destructive"
                      data-testid={`button-delete-planning-what-do-${item.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => handleAddPlanningItem("what_do")}
                  data-testid="button-add-what-do"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Add Another Record
                </Button>
              </div>

              {/* Resources and Budget */}
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <DollarSign className="w-4 h-4 text-primary" />
                  <h3 className="text-base font-semibold">Resources and Budget</h3>
                </div>
                {planningItems.filter(item => item.type === "resources_budget").map((item) => (
                  <div key={item.id} className="flex items-center gap-2">
                    <Input
                      defaultValue={item.content}
                      onBlur={(e) => {
                        e.stopPropagation();
                        if (e.target.value !== item.content) {
                          handleUpdatePlanningItem(item.id, e.target.value);
                        }
                      }}
                      onKeyDown={(e) => {
                        e.stopPropagation();
                        if (e.key === "Enter") {
                          e.preventDefault();
                          (e.target as HTMLInputElement).blur();
                        }
                      }}
                      placeholder="Enter resource or budget item..."
                      disabled={!!item.convertedToTaskId}
                      data-testid={`input-planning-resources-${item.id}`}
                    />
                    {item.convertedToTaskId ? (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled
                        className="flex-shrink-0"
                        data-testid={`button-task-created-resources-${item.id}`}
                      >
                        <CheckSquare className="w-4 h-4 mr-1" />
                        Task Created
                      </Button>
                    ) : (
                      <Button
                        type="button"
                        variant="default"
                        size="sm"
                        onClick={() => handleOpenCreateTask(item.content, item.id)}
                        disabled={!item.content?.trim()}
                        className="flex-shrink-0"
                        data-testid={`button-create-task-resources-${item.id}`}
                      >
                        Create Task
                      </Button>
                    )}
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDeletePlanningItem(item.id)}
                      className="text-muted-foreground hover:text-destructive"
                      data-testid={`button-delete-planning-resources-${item.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => handleAddPlanningItem("resources_budget")}
                  data-testid="button-add-resources-budget"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Add Another Record
                </Button>
              </div>
            </div>

            {/* Why Section */}
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-semibold">Why</h3>
                <p className="text-sm text-muted-foreground">
                  Explain how this will help you, someone else or improve your life
                </p>
              </div>
              
              <FormField
                control={form.control}
                name="whyReason1"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Input
                        placeholder="Reason 1"
                        {...field}
                        value={field.value || ""}
                        data-testid="input-edit-why-reason-1"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="whyReason2"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Input
                        placeholder="Reason 2"
                        {...field}
                        value={field.value || ""}
                        data-testid="input-edit-why-reason-2"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="whyReason3"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Input
                        placeholder="Reason 3"
                        {...field}
                        value={field.value || ""}
                        data-testid="input-edit-why-reason-3"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="whyReason4"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Input
                        placeholder="Reason 4"
                        {...field}
                        value={field.value || ""}
                        data-testid="input-edit-why-reason-4"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="whyReason5"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Input
                        placeholder="Reason 5"
                        {...field}
                        value={field.value || ""}
                        data-testid="input-edit-why-reason-5"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Category & Priority */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="category"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Category</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger data-testid="select-edit-goal-category">
                          <SelectValue placeholder="Select category" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {categories.filter(c => c.scope === "goals").length === 0 ? (
                          <SelectItem value="Uncategorized">Uncategorized</SelectItem>
                        ) : (
                          categories.filter(c => c.scope === "goals" && c.name).map((category) => (
                            <SelectItem key={category.id} value={category.name!}>
                              <span className="flex items-center gap-2">
                                <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: category.color }} />
                                {category.name}
                              </span>
                            </SelectItem>
                          ))
                        )}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="priority"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Priority</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger data-testid="select-edit-goal-priority">
                          <SelectValue placeholder="Select priority" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="high">High Priority</SelectItem>
                        <SelectItem value="medium">Medium Priority</SelectItem>
                        <SelectItem value="low">Low Priority</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Progress Tracking */}
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-semibold">Progress Tracking</h3>
                <p className="text-sm text-muted-foreground">
                  Choose how you want to track progress for this goal
                </p>
              </div>

              <FormField
                control={form.control}
                name="progressCalculationType"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Progress Calculation Method</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger data-testid="select-edit-progress-type">
                          <SelectValue placeholder="Select calculation method" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="manual">Manual - Set progress percentage manually</SelectItem>
                        <SelectItem value="milestones">Milestone-Based - Calculated from milestone completion</SelectItem>
                        <SelectItem value="tasks">Sub-Task-Based - Calculated from sub-task completion</SelectItem>
                        <SelectItem value="habits">Habit Count - Track habit click completions</SelectItem>
                        <SelectItem value="custom">Custom Measured - Track specific numbers or amounts</SelectItem>
                        <SelectItem value="trackerx">TrackerX - Measure with a TrackerX record (e.g., calories, carbs)</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* TrackerX record picker (shown only for trackerx progress type) */}
              {progressType === "trackerx" && (
                <>
                  <TrackerXMeasurementSection
                    enabled={progressType === "trackerx"}
                    selectedTrackerIds={selectedTrackerIds}
                    onToggleTracker={(id) => {
                      setSelectedTrackerIds((prev) =>
                        prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
                      );
                      setTrackerError("");
                    }}
                    newTrackers={newTrackers}
                    onNewTrackersChange={(list) => { setNewTrackers(list); setTrackerError(""); }}
                    currentGoalId={goal.id}
                  />
                  {trackerError && (
                    <p className="text-sm text-destructive" data-testid="text-tracker-error">{trackerError}</p>
                  )}
                </>
              )}

              {/* Show manual progress input only for manual type */}
              {progressType === "manual" && (
                <FormField
                  control={form.control}
                  name="progress"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Progress (%)</FormLabel>
                      <FormControl>
                        <NumberField
                          min={0}
                          max={100}
                          emptyValue={0}
                          value={field.value}
                          onValueChange={(v) => field.onChange(v ?? 0)}
                          name={field.name}
                          onBlur={field.onBlur}
                          ref={field.ref}
                          data-testid="input-edit-goal-progress"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

              {/* Habit Target Count (shown only for habit progress type) */}
              {progressType === "habits" && (
                <FormField
                  control={form.control}
                  name="habitTargetCount"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Target Habit Count</FormLabel>
                      <FormControl>
                        <NumberField
                          min={1}
                          placeholder="e.g., 100 for 100 habit clicks = 100%"
                          value={field.value || undefined}
                          onValueChange={field.onChange}
                          name={field.name}
                          onBlur={field.onBlur}
                          ref={field.ref}
                          data-testid="input-edit-habit-target-count"
                        />
                      </FormControl>
                      <p className="text-xs text-muted-foreground">
                        How many habit completions equal 100% progress?
                      </p>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

              {/* Custom Goal Fields (shown only for custom progress type) */}
              {progressType === "custom" && (
                <>
                  <FormField
                    control={form.control}
                    name="customGoalType"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Measurement Type</FormLabel>
                        <Select onValueChange={field.onChange} value={field.value}>
                          <FormControl>
                            <SelectTrigger data-testid="select-edit-custom-goal-type">
                              <SelectValue placeholder="Select measurement type" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="number">Number (e.g., pounds, miles, calls)</SelectItem>
                            <SelectItem value="currency">Currency (e.g., dollars, revenue)</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="customGoalTitle"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>What are you measuring?</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="e.g., Pounds Lost, Sales Calls Made, Revenue Generated"
                            {...field}
                            value={field.value || ""}
                            data-testid="input-edit-custom-goal-title"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="customGoalTarget"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Target Amount</FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            placeholder="e.g., 20 for lose 20 pounds"
                            {...field}
                            onChange={(e) => field.onChange(parseFloat(e.target.value) || undefined)}
                            value={field.value || ""}
                            data-testid="input-edit-custom-goal-target"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </>
              )}

              {/* Info messages for auto-calculated progress types */}
              {progressType === "milestones" && (
                <div className="text-sm text-muted-foreground bg-muted/50 p-3 rounded-md">
                  ℹ️ Progress will be automatically calculated based on milestone completion percentage
                </div>
              )}
              {progressType === "tasks" && (
                <div className="text-sm text-muted-foreground bg-muted/50 p-3 rounded-md">
                  ℹ️ Progress will be automatically calculated based on task completion percentage
                </div>
              )}
            </div>

            {/* Status */}
            <FormField
              control={form.control}
              name="status"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Status</FormLabel>
                  <Select onValueChange={(value) => {
                    field.onChange(value);
                    form.setValue("isFutureGoal", value === "future");
                  }} value={field.value}>
                    <FormControl>
                      <SelectTrigger data-testid="select-edit-goal-status">
                        <SelectValue placeholder="Select status" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="completed">Completed</SelectItem>
                      <SelectItem value="paused">Paused</SelectItem>
                      <SelectItem value="future">Future Goal</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Future Goal Toggle */}
            <FormField
              control={form.control}
              name="isFutureGoal"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                  <div className="space-y-0.5">
                    <FormLabel className="text-base flex items-center gap-2">
                      <Clock className="h-4 w-4" />
                      Future Goal
                    </FormLabel>
                    <div className="text-sm text-muted-foreground">
                      Mark this as a goal you plan to work on later
                    </div>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={(checked) => {
                        field.onChange(checked);
                        form.setValue("status", checked ? "future" : "active");
                      }}
                      data-testid="switch-edit-future-goal"
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            {/* Deadline & Target */}
            <div className={`grid gap-4 ${progressType === "custom" ? "grid-cols-1" : "grid-cols-1 sm:grid-cols-2"}`}>
              <FormField
                control={form.control}
                name="deadline"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Target Deadline</FormLabel>
                    <div className="flex gap-2">
                      <FormControl>
                        <Input
                          type="date"
                          {...field}
                          data-testid="input-edit-goal-deadline"
                        />
                      </FormControl>
                      {field.value && (
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          onClick={() => field.onChange("")}
                          data-testid="button-clear-deadline"
                          title="Clear deadline"
                        >
                          ×
                        </Button>
                      )}
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {progressType !== "custom" && (
                <FormField
                  control={form.control}
                  name="targetValue"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Target Value (Optional)</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="e.g., 10000, 24 books, 21km"
                          {...field}
                          value={field.value || ""}
                          data-testid="input-edit-goal-target-value"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}
            </div>

            {/* Time Cost Section */}
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <FormLabel className="mb-0">Time Cost</FormLabel>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <HelpCircle className="h-4 w-4 text-muted-foreground cursor-help" />
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs">
                    <p>The estimated time commitment needed to accomplish this goal. Enter the hours you'll dedicate per day, week, or month.</p>
                  </TooltipContent>
                </Tooltip>
              </div>
              <p className="text-xs text-muted-foreground">Enter hours as decimals (e.g., 0.5 = 30 min, 1.5 = 1 hr 30 min)</p>
              <div className="grid grid-cols-3 gap-2 items-end">
                <FormField
                  control={form.control}
                  name="timeCost"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <Input
                          type="number"
                          min="0"
                          step="0.1"
                          placeholder="e.g. 0.5"
                          {...field}
                          value={field.value || ""}
                          onChange={(e) => field.onChange(e.target.value ? parseFloat(e.target.value) : undefined)}
                          data-testid="input-edit-goal-time-cost"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="timeCostFrequency"
                  render={({ field }) => (
                    <FormItem>
                      <Select onValueChange={field.onChange} value={field.value || ""}>
                        <FormControl>
                          <SelectTrigger data-testid="select-edit-time-cost-frequency">
                            <SelectValue placeholder="Frequency" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="daily">Daily</SelectItem>
                          <SelectItem value="weekly">Weekly</SelectItem>
                          <SelectItem value="monthly">Monthly</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <div className="text-sm text-muted-foreground pb-2">
                  {(() => {
                    const timeCost = form.watch("timeCost");
                    const frequency = form.watch("timeCostFrequency");
                    const deadline = form.watch("deadline");
                    
                    if (!timeCost || !frequency || !deadline) return null;
                    
                    const deadlineDate = new Date(deadline);
                    const today = new Date();
                    const diffTime = deadlineDate.getTime() - today.getTime();
                    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                    
                    if (diffDays <= 0) return <span className="text-destructive">Past deadline</span>;
                    
                    let totalHours = 0;
                    if (frequency === "daily") {
                      totalHours = timeCost * diffDays;
                    } else if (frequency === "weekly") {
                      totalHours = timeCost * Math.ceil(diffDays / 7);
                    } else if (frequency === "monthly") {
                      totalHours = timeCost * Math.ceil(diffDays / 30);
                    }
                    
                    return <span className="font-medium">= {totalHours} hrs total</span>;
                  })()}
                </div>
              </div>
            </div>

            {/* Current Value */}
            <FormField
              control={form.control}
              name="currentValue"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Current Value (Optional)</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="e.g., 5000, 12 books, 10km"
                      {...field}
                      value={field.value || ""}
                      data-testid="input-edit-goal-current-value"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Actions */}
            <div className="flex items-center justify-end space-x-3 pt-4 border-t border-border">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                data-testid="button-cancel-edit-goal"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={updateGoalMutation.isPending}
                className="bg-primary hover:bg-primary/90 text-primary-foreground"
                data-testid="button-submit-edit-goal"
              >
                {updateGoalMutation.isPending ? "Updating..." : "Update Goal"}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>

      {/* Create Task Dialog - Full Page */}
      <Dialog open={createTaskDialogOpen} onOpenChange={setCreateTaskDialogOpen}>
        <DialogContent className="w-screen h-screen max-w-none max-h-none m-0 rounded-none overflow-y-auto" data-testid="modal-create-task-from-planning">
          <DialogHeader>
            <DialogTitle>Create Task from Planning Item</DialogTitle>
            <DialogDescription>Create a new task linked to your goal.</DialogDescription>
          </DialogHeader>
          <Form {...taskForm}>
            <form onSubmit={taskForm.handleSubmit(onTaskSubmit)} className="space-y-4">
              {/* Connected Goal Display */}
              <div className="p-4 bg-muted rounded-lg border">
                <div className="flex items-center gap-2 mb-1">
                  <Target className="w-4 h-4 text-primary" />
                  <span className="text-sm font-medium">Connected to Goal</span>
                </div>
                <p className="text-base font-semibold text-primary">{goal.title}</p>
              </div>

              <FormField
                control={taskForm.control}
                name="title"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Title *</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="Task title" data-testid="input-task-title" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={taskForm.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description</FormLabel>
                    <FormControl>
                      <Textarea
                        {...field}
                        value={field.value || ""}
                        placeholder="Task description (optional)"
                        rows={3}
                        data-testid="input-task-description"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={taskForm.control}
                name="category"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Category</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value || "Uncategorized"}>
                      <FormControl>
                        <SelectTrigger data-testid="select-task-category">
                          <SelectValue placeholder="Select category" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="Uncategorized">Uncategorized</SelectItem>
                        <CategorySelectGroups
                          categories={categories.filter(cat => cat.name !== "Uncategorized")}
                          renderContent={(category) => (
                            <span className="flex items-center gap-2">
                              <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: category.color ?? undefined }} />
                              {category.name}
                            </span>
                          )}
                        />
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={taskForm.control}
                name="priority"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Priority</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value || "medium"}>
                      <FormControl>
                        <SelectTrigger data-testid="select-task-priority">
                          <SelectValue placeholder="Select priority" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="low">Low</SelectItem>
                        <SelectItem value="medium">Medium</SelectItem>
                        <SelectItem value="high">High</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={taskForm.control}
                name="dueDate"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Due Date</FormLabel>
                    <FormControl>
                      <Input type="date" {...field} data-testid="input-task-due-date" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Tags */}
              <div className="space-y-2">
                <FormLabel>Tags</FormLabel>
                <div className="flex gap-2">
                  <Input
                    placeholder="Add a tag..."
                    value={currentTaskTag}
                    onChange={(e) => setCurrentTaskTag(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        handleAddTaskTag();
                      }
                    }}
                    data-testid="input-task-tag"
                  />
                  <Button type="button" variant="outline" onClick={handleAddTaskTag} data-testid="button-add-tag">
                    Add
                  </Button>
                </div>
                {taskTags.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {taskTags.map((tag) => (
                      <Badge key={tag} variant="secondary" className="flex items-center gap-1">
                        {tag}
                        <button
                          type="button"
                          onClick={() => handleRemoveTaskTag(tag)}
                          className="ml-1 hover:text-destructive"
                          data-testid={`button-remove-tag-${tag}`}
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                )}
              </div>

              {/* Assigned To */}
              <div className="space-y-2">
                <FormLabel>Assigned To</FormLabel>
                <Select value={taskAssignedTo} onValueChange={(value: "Me" | "Others") => setTaskAssignedTo(value)}>
                  <SelectTrigger data-testid="select-task-assigned-to">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Me">Me</SelectItem>
                    <SelectItem value="Others">Others</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {taskAssignedTo === "Others" && (
                <FormField
                  control={taskForm.control}
                  name="assignedToOther"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Assign to (Name)</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="Enter name..." data-testid="input-task-assigned-to-other" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

              <FormField
                control={taskForm.control}
                name="showOnMainTaskPage"
                render={({ field }) => (
                  <FormItem className="flex items-center gap-3 pt-2">
                    <FormLabel className="mb-0">Show on Tasks Page</FormLabel>
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                        data-testid="switch-task-show-on-main"
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              <DialogFooter className="pt-4 border-t">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setCreateTaskDialogOpen(false)}
                  data-testid="button-cancel-create-task"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={createTaskMutation.isPending}
                  data-testid="button-submit-create-task"
                >
                  {createTaskMutation.isPending ? "Creating..." : "Create Task"}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}
